import { useCallback, useEffect, useState } from 'react'

import {
  Button,
  Center,
  Flex,
  FormLabel as ChakraFormLabel,
  Text,
  VStack,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type { NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { getReturnToSearchParam } from 'api/utils/routes'
import { ClaimVenueAccount } from 'components/ClaimVenueAccount/ClaimVenueAccount'
import { Card } from 'components/common/Card'
import { NextLink } from 'components/common/NextLink'
import { FormPasswordInput, FormTextInput } from 'components/Inputs'
import { Route, SignInErrorReason } from 'constants/routes'
import {
  ClaimVenueAccountMutation,
  Region,
  useClaimVenueAccountMutation,
} from 'generated/generated-graphql'
import { withRedirect } from 'hoc/withRedirect'
import { useInvitationLink } from 'hooks/useInvitationLink'
import { getAuthorizationParamsFromLocalStorage } from 'integrations/authorizationParams'
import { useAuth } from 'providers/UserProvider'
import { useErrorToast } from 'utils/toast'

import { TranslateFunction } from '../../next-translate'

enum FieldName {
  Email = 'email',
  Password = 'password',
}

const CreateSignUpFormValidationSchema = (t: TranslateFunction<'common'>) =>
  yup
    .object({
      [FieldName.Email]: yup
        .string()
        .email(t('WRONG_EMAIL_FORMAT'))
        .label(t('EMAIL'))
        .required(),
      [FieldName.Password]: yup.string().label(t('signIn.password')).required(),
    })
    .required()

type FormValues = yup.InferType<
  ReturnType<typeof CreateSignUpFormValidationSchema>
>

type UnclaimedVenueAccount = NonNullable<
  ClaimVenueAccountMutation['claimVenueAccount']
>

const SignIn: NextPageWithLayout = () => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { signInWithEmailAndPassword, isLoading } = useAuth()
  const errorToast = useErrorToast()
  const [unclaimedVenueAccount, setUnclaimedVenueAccount] =
    useState<UnclaimedVenueAccount | null>(null)
  const [claimVenueAccount] = useClaimVenueAccountMutation()
  const methods = useForm<FormValues>({
    defaultValues: {
      [FieldName.Email]: '',
      [FieldName.Password]: '',
    },
    resolver: yupResolver(CreateSignUpFormValidationSchema(t)),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const { data } = useInvitationLink()

  useEffect(() => {
    // Preset Email if Invitation Link is present
    if (data?.invitationLink?.email) {
      methods.setValue(FieldName.Email, data.invitationLink.email)
    }

    const authorizationParams = getAuthorizationParamsFromLocalStorage()
    if (authorizationParams) {
      methods.setValue(FieldName.Email, authorizationParams.userEmail)
    }
  }, [data?.invitationLink, methods])

  const showUnitedKingdomVenueBlockedToast = useCallback(
    () =>
      errorToast({
        title: t('signIn.unitedKingdomVenueBlocked.title'),
        description: t('signIn.unitedKingdomVenueBlocked.description'),
      }),
    [errorToast, t]
  )

  useEffect(() => {
    if (
      router.query.errorReason === SignInErrorReason.UnitedKingdomVenueBlocked
    ) {
      showUnitedKingdomVenueBlockedToast()
      void router.replace(Route.SignIn(), undefined, { shallow: true })
    }
  }, [router, showUnitedKingdomVenueBlockedToast])

  // Accounts created by accepting a match deal have a generated password, any password sends the set password email
  const claimVenueAccountIfUnclaimed = async (email: string) => {
    let account: UnclaimedVenueAccount | null | undefined
    try {
      const { data } = await claimVenueAccount({
        variables: { input: { email } },
      })
      account = data?.claimVenueAccount
    } catch {
      // e.g. rate limited, the default invalid credentials error is shown
      return false
    }

    if (!account) {
      return false
    }

    // Venues of the United Kingdom are not allowed to sign in
    if (account.region === Region.UnitedKingdom) {
      showUnitedKingdomVenueBlockedToast()
      return true
    }

    setUnclaimedVenueAccount(account)
    return true
  }

  const backToSignIn = () => {
    methods.resetField(FieldName.Password)
    setUnclaimedVenueAccount(null)
  }

  const formErrors = methods.formState.errors

  const setInvalidCredentialsErrors = () => {
    // Both fields are highlighted because Firebase does not reveal which one is wrong
    methods.setError(FieldName.Email, { type: 'invalidCredentials' })
    methods.setError(FieldName.Password, {
      type: 'invalidCredentials',
      message: t('TRANSLATION_136'),
    })
  }

  const submit = async ({ email, password }: FormValues) => {
    setIsSubmitting(true)
    try {
      await signInWithEmailAndPassword({
        email,
        password,
        handleInvalidCredentials: async () => {
          const isHandled = await claimVenueAccountIfUnclaimed(email)
          if (!isHandled) {
            setInvalidCredentialsErrors()
          }
          return isHandled
        },
      })
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  if (unclaimedVenueAccount) {
    return (
      <Center
        width="100vw"
        minHeight="100vh"
        backgroundColor="brand.ghostWhite"
      >
        <ClaimVenueAccount
          email={unclaimedVenueAccount.email}
          integrationType={unclaimedVenueAccount.integrationType}
          resendAvailableInSeconds={
            unclaimedVenueAccount.resendAvailableInSeconds ?? 0
          }
          onBackToSignIn={backToSignIn}
        />
      </Center>
    )
  }

  return (
    <Center width="100vw" minHeight="100vh" backgroundColor="brand.ghostWhite">
      <Card m={2}>
        <FormProvider {...methods}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={methods.handleSubmit(submit)}>
            <VStack alignItems="center" spacing={4}>
              <Text
                width="full"
                textAlign="center"
                fontFamily="Cocogoose Pro"
                fontSize="2xl"
                color="brand.ultramarine"
              >
                {t('signIn.headingText')}
              </Text>
              <FormTextInput
                id={FieldName.Email}
                label={t('EMAIL')}
                placeholder={t('EMAIL_PLACEHOLDER')}
                errorMessage={formErrors.email?.message}
                isInvalid={Boolean(formErrors.email)}
                isReadOnly={Boolean(data?.invitationLink?.email)}
                isDisabled={Boolean(data?.invitationLink?.email)}
              />
              <Flex width="100%" justify="space-between" align="end">
                <ChakraFormLabel variant="input">
                  {t('signIn.password')}
                </ChakraFormLabel>
                <NextLink
                  href={Route.ForgotPassword()}
                  color="brand.ultramarine"
                  fontSize="xs"
                  tabIndex={-1}
                >
                  {t('signIn.forgotPassword')}
                </NextLink>
              </Flex>
              <FormPasswordInput
                id={FieldName.Password}
                placeholder={t('signIn.yourPassword')}
                errorMessage={formErrors.password?.message}
                isInvalid={Boolean(formErrors.password)}
              />
            </VStack>
            <VStack>
              <Button
                type="submit"
                isLoading={isLoading || isSubmitting}
                mt="30px"
              >
                {t('signIn.signInButtonLabel')}
              </Button>
              <NextLink
                href={Route.SignUp(getReturnToSearchParam(router))}
                color="brand.ultramarine"
                fontFamily="Proxima Nova Semibold"
                fontSize="sm"
              >
                {t('signIn.signUpLabel')}
              </NextLink>
            </VStack>
          </form>
        </FormProvider>
      </Card>
    </Center>
  )
}

export default withRedirect()(SignIn, true)
